import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';

import Notify from 'store/utils/Notify';
import ErrorHandler from 'store/utils/ErrorHandler';
import { getEntitiesRequest } from 'store/services/entities/api';
import { entityType } from 'types';
import ReplaceAllModal from 'components/ReplaceAllModal';
import SelectableList from 'components/SelectableList';
import EntityItem from './components/EntityItem';
import replaceEntityRequest from './api';

const EntitiesList = ({
  entities,
  selectedEntity,
  currentEntityIndex,
  selectEntity,
  deselectEntity,
}) => {
  const [entityToReplace, setEntityToReplace] = useState(undefined);
  const openModal = id => setEntityToReplace(id);
  const closeModal = () => setEntityToReplace(undefined);
  const [replaceEntity] = useMutation(replaceEntityRequest, {
    onSuccess: ({ data }) => {
      Notify.success({
        title: 'Entity was replaced successfully',
        description: ErrorHandler.getDetailError({ response: { data } }),
      });
    },
    onError: error => {
      Notify.error({
        title: 'Failed to replace entity',
        description: ErrorHandler.getDetailError(error),
      });
    },
    onSettled: closeModal,
  });
  const [fetchEntities, { isLoading }] = useMutation(async term => {
    const { data } = await getEntitiesRequest({
      name__icontains: term,
    });
    return data?.results?.map(({ name, id, archived }) => ({
      label: name,
      value: id,
      isDisabled: archived,
    }));
  });

  const handleOnReplace = newEntity =>
    replaceEntity({ selectedEntity: entityToReplace, newEntity });

  const selectableEntities = useMemo(
    () =>
      entities
        .filter(({ id }) => id !== entityToReplace)
        .map(({ id, name, archived }) => ({
          label: name,
          value: id,
          isDisabled: archived,
        })),
    [entities, entityToReplace],
  );

  return (
    <>
      <SelectableList
        currentItemIndex={currentEntityIndex}
        data-test="entities-list"
        deselectItem={deselectEntity}
        ItemComponent={<EntityItem triggerModal={openModal} />}
        items={entities}
        selectedItem={selectedEntity}
        selectItem={selectEntity}
      />
      {entityToReplace && (
        <ReplaceAllModal
          isLoading={isLoading}
          items={selectableEntities}
          loadOptions={fetchEntities}
          type="entity"
          onCancel={closeModal}
          onReplace={handleOnReplace}
        />
      )}
    </>
  );
};

EntitiesList.propTypes = {
  currentEntityIndex: PropTypes.number,
  deselectEntity: PropTypes.func,
  entities: PropTypes.arrayOf(entityType),
  selectedEntity: entityType,
  selectEntity: PropTypes.func,
};

export default EntitiesList;
